// Smart BOGOF / shipping goal state shared by the PDP and cart drawer.
// Liquid owns the offer maths and copy; this element owns progressive motion,
// expanded-detail memory and PDP refreshes after AJAX cart changes.

class SmartOfferEngine extends HTMLElement {
	connectedCallback() {
		if (this._connected) return;
		this._connected = true;
		this._controller = new AbortController();
		this._request = 0;

		this._restoreDetailsState();
		this.querySelector('details')?.addEventListener('toggle', (event) => this._rememberDetailsState(event), {
			signal: this._controller.signal,
		});

		requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-ready')));

		if (this.dataset.smartOfferContext !== 'product') return;

		this._sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
		if (!this._sectionId) return;

		// Deliberately NOT bundling this section into cart mutations: on PDPs
		// this element lives inside the whole main-product section, and section
		// rendering is section-granular, so bundling made every quantity tweak
		// download the full gallery/picker markup inside the cart response and
		// delayed the drawer update. The offer meter instead refreshes itself
		// asynchronously after the cart has settled; if another component has
		// already bundled this section, its HTML is used for free.
		document.addEventListener('cart:change', (event) => {
			const sectionHtml = event.detail?.cart?.sections?.[this._sectionId];
			if (sectionHtml) this._replaceFromHtml(sectionHtml);
			else this._refreshFromServer();
		}, { signal: this._controller.signal });

		document.addEventListener('cart:refresh', () => this._refreshFromServer(), {
			signal: this._controller.signal,
		});

		window.addEventListener('pageshow', (event) => {
			if (event.persisted) this._refreshFromServer();
		}, { signal: this._controller.signal });
	}

	disconnectedCallback() {
		this._controller?.abort();
		this._connected = false;
	}

	get _detailsKey() {
		return `blancspace.smartOfferDetails.${this.dataset.smartOfferContext || 'default'}`;
	}

	_restoreDetailsState() {
		const details = this.querySelector('details');
		if (!details) return;
		try {
			details.open = sessionStorage.getItem(this._detailsKey) === 'open';
		} catch (error) {
			// Storage can be unavailable in strict privacy contexts; closed is safe.
		}
	}

	_rememberDetailsState(event) {
		try {
			sessionStorage.setItem(this._detailsKey, event.currentTarget.open ? 'open' : 'closed');
		} catch (error) {
			// The control remains fully functional without persistence.
		}
	}

	_replaceFromHtml(html) {
		const documentFragment = new DOMParser().parseFromString(html, 'text/html');
		const next = documentFragment.querySelector('[data-smart-offer-context="product"]');
		if (next && this.isConnected) this.replaceWith(next);
	}

	async _refreshFromServer() {
		const request = ++this._request;
		try {
			window.bsMark?.('smart-offer-refetch');
			const url = new URL(window.location.href);
			url.searchParams.set('section_id', this._sectionId);
			const response = await fetch(url, { headers: { Accept: 'text/html' } });
			if (!response.ok || request !== this._request) return;
			const html = await response.text();
			window.bsMark?.('smart-offer-replace');
			this._replaceFromHtml(html);
		} catch (error) {
			// Keep the last accurate server-rendered state if refresh is unavailable.
		}
	}
}

if (!customElements.get('smart-offer-engine')) {
	customElements.define('smart-offer-engine', SmartOfferEngine);
}
